// Public-site hero. Full-viewport. "Happy blue" radial gradient — JoP primary
// blue brightest at the focal point, deepening to near-navy at the edges.
// Sparkles drift. Lobster Two script headline matches the handwritten energy
// of the current thejoyofpancakes.com brand without copying it directly.
import FairyDustField from "./components/FairyDustField";
import PancakeDrift from "./components/PancakeDrift";
import PanBar from "./components/PanBar";

// "Happy blue" radial — bright JoP primary at the focal point, deepening to
// near-black navy at the edges. Gives depth without going flat.
const HAPPY_BLUE_BG = `
  radial-gradient(ellipse 140% 95% at 50% 35%,
    #1f59db 0%,
    #1a4b9e 30%,
    #0e2e46 70%,
    #081a2a 100%)
`;

export default function Hero() {
  return (
    <section
      id="hero"
      style={{
        position: "relative",
        minHeight: "100vh",
        background: HAPPY_BLUE_BG,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        padding: "6rem 1rem 4rem",
        boxSizing: "border-box",
      }}
    >
      {/* Pancake drifters render BEHIND fairy dust so the sparkle layer
          still reads as the top ambient element over the artwork. */}
      <PancakeDrift />
      <FairyDustField density={80} seed={11} riseDistance={320} />

      {/* Bottom-edge glow — warm amber rising from the seam, feathered into
          soft pink. Uses orange (#f57600) rather than gold (#fcb31f) because
          gold's strong green channel (179/255) reads greenish when additively
          layered over navy. Orange keeps the warmth without the tint. */}
      <div aria-hidden="true" style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 1,
        background: `
          radial-gradient(ellipse 85% 22% at 50% 100%, rgba(245,118,0,0.24) 0%, rgba(239,83,126,0.12) 42%, transparent 68%)
        `,
      }} />

      <div style={{
        position: "relative",
        zIndex: 2,
        textAlign: "center",
        maxWidth: "860px",
      }}>
        <img
          src="/jop_logo_wide.png"
          alt="The Joy of Pancakes"
          fetchpriority="high"
          decoding="async"
          width={600}
          height={253}
          style={{
            width: "min(340px, 72vw)",
            height: "auto",
            display: "block",
            margin: "0 auto 1.2rem",
            filter: "drop-shadow(0 6px 28px rgba(0,0,0,0.4))",
          }}
        />

        <h1 style={{
          margin: "0 0 1.1rem",
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(2.6rem, 7vw, 4.8rem)",
          fontWeight: 700,
          lineHeight: 1.05,
          color: "var(--jop-gold)",
          textShadow: "0 3px 0 rgba(0,0,0,0.15), 0 4px 28px rgba(252,179,31,0.35)",
          letterSpacing: "-0.01em",
        }}>
          Your Golden Ticket Awaits.
        </h1>

        <PanBar width={140} height={5} margin="0 auto 1.6rem" />

        <p style={{
          margin: "0 auto",
          maxWidth: "640px",
          fontSize: "clamp(1.05rem, 2.1vw, 1.25rem)",
          lineHeight: 1.6,
          color: "var(--jop-cream)",
          fontFamily: "var(--jop-display)",
          fontWeight: 500,
          opacity: 0.96,
          textShadow: "0 1px 6px rgba(0,0,0,0.35)",
        }}>
          We invented live pancake art. At your event, we paint in batter, flip — and the room erupts! They eat the art; they keep it forever.<br /><em style={{ display: "inline-block", marginTop: "0.6rem", color: "var(--jop-pink)", fontStyle: "italic", fontWeight: 700, textShadow: "0 1px 10px rgba(239,83,126,0.4)" }}>You found us.</em>
        </p>
      </div>

      {/* Bouncing scroll indicator — scrolls to the next section ("flip"). */}
      <a
        href="#flip"
        onClick={(e) => {
          e.preventDefault();
          const target = document.getElementById("flip");
          if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
          } else {
            // Fallback: scroll one viewport down.
            window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
          }
        }}
        style={{
          position: "absolute",
          bottom: "2rem",
          left: "50%",
          transform: "translateX(-50%)",
          color: "var(--jop-cream)",
          textDecoration: "none",
          fontSize: "0.82rem",
          fontFamily: "var(--jop-display)",
          fontWeight: 600,
          letterSpacing: "0.12em",
          opacity: 0.72,
          textAlign: "center",
          zIndex: 2,
        }}
      >
        <div style={{ marginBottom: "0.25rem" }}>MORE BELOW</div>
        <div style={{
          fontSize: "1.5rem",
          animation: "jopHeroBounce 2s ease-in-out infinite",
        }}>⌄</div>
      </a>

      <style>{`
        @keyframes jopHeroBounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(8px); }
        }
      `}</style>
    </section>
  );
}
